// Phase 2.3 — Telecom Findings & Recommendations template registry.
// 4 severity variants for each of the 44 Infrastructure Security controls
// (12 generic B1–B4 + 32 telecom v2 C1–C8) seeded from the Phase 2.2.6 metadata.
//
// Editorial only. Does NOT affect scoring (v1_flat remains active).
// Used by the reviewer Findings UI to pre-fill `assessment_findings` rows
// via `reviewer_upsert_infrastructure_finding`.
//
// Parity with the catalog is enforced by:
//   src/configs/controlGuidance/__tests__/infrastructureFindingTemplates.spec.ts

import { INFRASTRUCTURE_CONTROL_METADATA } from "./infrastructureControlMetadata";
import { getInfrastructureRemediation } from "./infrastructureRemediationLibrary";

export type FindingSeverity = "Critical" | "High" | "Medium" | "Low";
export type PriorityLevel = "P1" | "P2" | "P3" | "P4" | "Immediate" | "Quick Win" | "Strategic";
export type RiskLevel = "Critical" | "High" | "Medium" | "Moderate" | "Low";

export interface InfrastructureFindingTemplate {
  controlKey: string;
  severity: FindingSeverity;
  finding_title: string;
  recommended_action: string;
  recommended_measures: string;
  remediation_path: string;
  priority_level: PriorityLevel;
  timeframe: string;
  risk_level: RiskLevel;
}

// Severity → priority/timeframe/risk locked to the 4-tier residual risk matrix.
// Reviewers can override any field after a template is applied.
const SEVERITY_DEFAULTS: Record<
  FindingSeverity,
  { priority: PriorityLevel; timeframe: string; risk: RiskLevel }
> = {
  Critical: { priority: "P1", timeframe: "0–30 days",   risk: "Critical" },
  High:     { priority: "P2", timeframe: "30–90 days",  risk: "High" },
  Medium:   { priority: "P3", timeframe: "90–180 days", risk: "Medium" },
  Low:      { priority: "P4", timeframe: "180–365 days", risk: "Low" },
};

const SEVERITY_TITLE_PREFIX: Record<FindingSeverity, string> = {
  Critical: "Critical gap",
  High:     "Material gap",
  Medium:   "Improvement opportunity",
  Low:      "Minor refinement",
};

const SEVERITY_ACTION_TONE: Record<FindingSeverity, string> = {
  Critical: "Remediate immediately. Treat as a top-priority risk to network integrity and service continuity.",
  High:     "Plan remediation within the current operating cycle. Significant exposure to telecom service availability or data confidentiality.",
  Medium:   "Schedule remediation in the next planning cycle. Reduces risk and improves operational maturity.",
  Low:      "Address as part of continuous improvement. Hardens the control and improves auditability.",
};

// Build a default 4-variant template set from the Phase 2.2.6 metadata.
// Reviewers can edit any field after Apply Template.
export const INFRASTRUCTURE_FINDING_TEMPLATES: InfrastructureFindingTemplate[] =
  (() => {
    const out: InfrastructureFindingTemplate[] = [];
    const severities: FindingSeverity[] = ["Critical", "High", "Medium", "Low"];
    for (const meta of Object.values(INFRASTRUCTURE_CONTROL_METADATA)) {
      // Phase 2.8.2 — Telecom controls (C1–C8) use the control-specific
      // remediation library. Generic (B1–B4) controls fall back to the
      // metadata-derived generic template.
      const override = getInfrastructureRemediation(meta.controlKey);
      for (const sev of severities) {
        const defaults = SEVERITY_DEFAULTS[sev];
        const titlePrefix = SEVERITY_TITLE_PREFIX[sev];
        const genericMeasures =
          (meta.evidenceExamples.length
            ? `Evidence to obtain or produce:\n- ${meta.evidenceExamples.join("\n- ")}`
            : "") +
          (meta.telecomStandards.length
            ? `\n\nReference standards:\n- ${meta.telecomStandards.join("\n- ")}`
            : "");
        const genericPath =
          sev === "Critical"
            ? `1) Stop-gap mitigation within 7 days. 2) Full remediation per "implemented" rubric. 3) Independent verification.`
            : sev === "High"
            ? `1) Gap analysis. 2) Remediation plan with owner & date. 3) Verification against rubric. 4) Evidence archived.`
            : sev === "Medium"
            ? `1) Document current state. 2) Plan incremental improvement. 3) Update evidence repository.`
            : `1) Capture in continuous-improvement backlog. 2) Address during next operational review.`;

        out.push({
          controlKey: meta.controlKey,
          severity: sev,
          finding_title: `${titlePrefix} — ${meta.label}`,
          // Severity tone is always prepended so the urgency is unambiguous,
          // followed by the control-specific corrective action when available.
          recommended_action: override
            ? `${SEVERITY_ACTION_TONE[sev]} ${override.recommended_action}`
            : `${SEVERITY_ACTION_TONE[sev]} ${meta.assessmentRubric.implemented}`,
          recommended_measures: override?.recommended_measures || genericMeasures,
          remediation_path: override?.remediation_path || genericPath,
          priority_level: defaults.priority,
          timeframe: defaults.timeframe,
          risk_level: defaults.risk,
        });
      }
    }
    return out;
  })();

export function getInfrastructureFindingTemplates(
  controlKey: string
): InfrastructureFindingTemplate[] {
  return INFRASTRUCTURE_FINDING_TEMPLATES.filter((t) => t.controlKey === controlKey);
}

export function getInfrastructureFindingTemplate(
  controlKey: string,
  severity: FindingSeverity
): InfrastructureFindingTemplate | undefined {
  return INFRASTRUCTURE_FINDING_TEMPLATES.find(
    (t) => t.controlKey === controlKey && t.severity === severity
  );
}

// ── Legacy compatibility shim ─────────────────────────────────────────────
// Pre-Phase-2.3 code imported `INFRASTRUCTURE_FINDING_TEMPLATES[control_key]`
// expecting a single object. Provide a lookup map of the "High" variant so
// existing call-sites (InfrastructureSecurityTab → reviewer_sync_infrastructure_finding)
// keep working without behavioral change.
export const LEGACY_FINDING_TEMPLATES_BY_KEY: Record<
  string,
  {
    finding_title: string;
    recommended_action: string;
    recommended_measures: string;
    remediation_path: string;
    priority_level: PriorityLevel;
    timeframe: string;
    risk_level: RiskLevel;
  }
> = (() => {
  const map: Record<string, any> = {};
  for (const tpl of INFRASTRUCTURE_FINDING_TEMPLATES) {
    if (tpl.severity !== "High") continue;
    const { controlKey: _ck, severity: _sv, ...rest } = tpl;
    map[tpl.controlKey] = rest;
  }
  return map;
})();
